'use strict'
// React・React関連
import * as React from 'react'
import { useCallback, useEffect, useReducer, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Link as RouterLink } from 'react-router-dom'

// MUIなどの外部UIライブラリ
import { Link } from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'

// DnD-kit関連
import { DndContext, closestCenter } from '@dnd-kit/core'
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

// 日付関連
import { registerLocale } from 'react-datepicker'
import { format } from 'date-fns'
import ja from 'date-fns/locale/ja'
import 'react-datepicker/dist/react-datepicker.css'

// ロガー・共通ユーティリティ
import { errorLog } from '../../../common/logger/Logger'
import { viewId, titleName, masterClassification } from '../../../_constants/Code'
import { getMessage } from '../../../common/utils/MessageUtils'
import { useModifyClassList as modifyClassList } from '../../../common/utils/CSSUtils'

// Redux関連共通コンポーネント
import Forbidden from '../../../_components/Forbidden'
import CustomTooltip from '../../../_components/CustomTooltip'
import { useModal, useModalExtra } from '../../../_components/Modal'

// Immer・lodash
import { produce } from 'immer'
import isEqual from 'lodash.isequal'

// サービス・API呼び出し
import { fetchMaster, fetchSegment, fetchStandardFormatUsageItems, updateSegment } from './extra/services'

// 共通関数・ユーティリティ
import { setSegmentInfoInitialData, getInitialDataBySegment } from './extra/common'
import { getConflictEditedSegmentIds } from './extra/segmentUtils'

// コンポーネント（同階層）
import SegmentModalContent from './components/SegmentModalContent'
import { ShowErrors, ShowServerErrors } from './components/ShowErrors'

// カスタムボタン（共通）
import { SpinnerButton, DeleteButton } from '../../common/CustomButton'

// 日本語ロケールを登録
registerLocale('ja', ja)

/*==================================
  状態管理・Reducer
===================================*/
/**
 * セグメントの状態を管理するReducer関数
 *
 * @param {SegmentState[]} state - 現在の状態配列
 * @param {Action} action - 実行するアクション
 * @returns {SegmentState[]} 更新後の状態配列
 */
const segmentReducer = (state, action) => {
  return produce(state, (draft) => {
    switch (action.type) {
      case 'INIT_STATE': {
        const apiData = action.payload
        draft.splice(0, draft.length)
        apiData.forEach((item) => {
          draft.push({
            ...item,
            uiState: {
              deleted: false,
              hasChanges: false,
              previousHasChanges: false,
              registered: true,
              hasError: false,
              initialSegmentName: item.segmentName
            }
          })
        })
        break
      }
      case 'MOVE_ITEM': {
        const { oldIndex, newIndex } = action.payload
        const newDraft = arrayMove(draft, oldIndex, newIndex)
        // 新しい順位に置き換え
        draft.splice(0, draft.length, ...newDraft)
        break
      }
      case 'UPDATE_ITEM': {
        const updateItem = action.payload
        const item = draft.find((item) => item.searchKey === updateItem.searchKey)
        if (item) {
          // 更新内容比較
          const initialItem = getInitialDataBySegment(item.searchKey) || {}
          const initialData = {
            segmentCode: initialItem.segmentCode,
            segmentName: initialItem.segmentName,
            validStartDate: initialItem.validStartDate,
            validEndDate: initialItem.validEndDate
          }
          const updateData = {
            segmentCode: updateItem.segmentCode,
            segmentName: updateItem.segmentName,
            validStartDate: updateItem.validStartDate,
            validEndDate: updateItem.validEndDate
          }

          const hasChanges = !isEqual(initialData, updateData)
          item.uiState.hasChanges = hasChanges

          const { uiState, ...rest } = updateItem
          Object.assign(item, rest)
        }
        break
      }
      case 'DELETE_ITEM': {
        const searchKey = action.payload
        const index = draft.findIndex((item) => item.searchKey === searchKey)
        if (index !== -1) {
          if (draft[index].uiState.registered) {
            // 削除前の変更状態を保持
            draft[index].uiState.previousHasChanges = draft[index].uiState.hasChanges
            draft[index].uiState.hasChanges = true
            draft[index].uiState.deleted = true
          } else {
            draft.splice(index, 1)
          }
        }
        break
      }
      case 'RESTORE_ITEM': {
        const searchKey = action.payload
        const item = draft.find((item) => item.searchKey === searchKey)
        if (item && item.uiState.deleted) {
          // 削除前の変更状態に戻す
          item.uiState.hasChanges = item.uiState.previousHasChanges || false
          item.uiState.deleted = false
        }
        break
      }
      case 'ADD_ITEM': {
        const newItem = action.payload
        draft.push({
          ...newItem,
          uiState: {
            deleted: false,
            hasChanges: false,
            previousHasChanges: false,
            registered: false,
            hasError: false
          }
        })
        break
      }
      case 'SET_CLIENT_ERROR': {
        const errorStateIds = action.payload
        draft.forEach((item) => {
          item.uiState.hasError = errorStateIds.includes(item.searchKey)
        })
        break
      }
      default:
        return state
    }
  })
}

/**
 * 更新前のAPI送信用データ整形
 *
 * @param {SegmentState[]} segmentState - 現在の状態管理配列
 * @returns {Object[]} API送信用配列
 */
function prepareFileData(segmentState) {
  // 削除されていないアイテムだけ抽出
  const activeItems = segmentState.filter((item) => !item.uiState.deleted)
  // 削除されたアイテムだけ抽出
  const deletedItems = segmentState.filter((item) => item.uiState.deleted)

  // 削除されていないアイテムをmap、display_orderは連番（1始まり）
  const activeData = activeItems.map((item, index) => {
    const { id, uiState, ...rest } = item
    return {
      search_key: item.searchKey || null,
      segment_code: item.segmentCode || null,
      segment_name: item.segmentName,
      valid_start_date: item.validStartDate || null,
      valid_end_date: item.validEndDate || null,
      display_order: index + 1,
      is_delete: false
    }
  })

  // 削除されたアイテムをmap
  const deletedData = deletedItems.map((item) => {
    const { id, uiState, ...rest } = item
    return {
      search_key: item.searchKey || null,
      segment_code: item.segmentCode || null,
      segment_name: item.segmentName,
      valid_start_date: item.validStartDate || null,
      valid_end_date: item.validEndDate || null,
      display_order: null, // 削除対象は表示順不要
      is_delete: true
    }
  })

  // activeDataの後ろにdeletedDataを結合して返す
  return [...activeData, ...deletedData]
}

/**
 * ドラッグ＆ドロップ可能なセグメント行コンポーネント
 *
 * @param {Object} props
 * @param {string} props.searchKey - セグメントのキー
 * @param {SegmentState} props.seg - セグメントの状態オブジェクト
 * @param {function(string):void} props.onDelete - 削除ハンドラ（キー指定）
 * @param {function(string):void} props.onRestore - 復元ハンドラ（キー指定）
 * @param {function(string, SegmentState=):void} props.openSegmentModal - セグメントモーダルを開く関数
 * @returns {JSX.Element} セグメント行のJSX
 */
const SortableItem = ({ searchKey, seg, onDelete, onRestore, openSegmentModal }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: searchKey })
  const isDisabled = seg.uiState?.deleted
  const lineThroughClass = isDisabled ? '!line-through !text-gray-900 !decoration-gray-900' : ''
  const errorClasses = 'outline outline-[1px] outline-[#f4392c]'
  const hasError = seg.uiState.hasError

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  }

  const disabledStyle = {
    pointerEvents: isDisabled ? 'none' : 'auto',
    cursor: isDisabled ? 'default' : 'pointer'
  }

  return (
    <tr
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={`${isDisabled ? 'bg-gray-300' : ''} ${hasError ? errorClasses : ''}`}
    >
      <td className="cursor-pointer order-icon" style={disabledStyle} {...listeners}>
        <MenuIcon />
      </td>
      <td className="cursor-default">
        <CustomTooltip title={seg.segmentName} useParentWidth={true}>
          {(ref) => (
            <Link
              className="w-full truncate !text-left"
              ref={ref}
              variant="button"
              underline="none"
              component={'button'}
              onClick={() => !isDisabled && openSegmentModal('update', seg)}
              style={disabledStyle}
              tabIndex={isDisabled ? -1 : 0}
              aria-disabled={isDisabled}
            >
              <span className={`font-Medium truncate ${lineThroughClass}`} style={{ textTransform: 'none' }}>
                {seg.segmentName}
              </span>
            </Link>
          )}
        </CustomTooltip>
      </td>
      <td className="cursor-default">
        <span className="cursor-text">
          {seg.validStartDate ? format(new Date(seg.validStartDate), 'yyyy年M月', { locale: ja }) : ''}
        </span>
      </td>
      <td className="cursor-default">
        <div className="flex justify-between">
          <span className="cursor-text">
            {seg.validEndDate ? format(new Date(seg.validEndDate), 'yyyy年M月', { locale: ja }) : ''}
          </span>
          <DeleteButton
            className="flex"
            onDelete={() => onDelete(seg.searchKey)}
            onRestore={() => onRestore(seg.searchKey)}
            isRestore={isDisabled}
          />
        </div>
      </td>
    </tr>
  )
}

/**
 * Dispatch関数を簡易化するカスタムフック
 *
 * @param {React.Dispatch<any>} dispatch - 元のdispatch関数
 * @returns {(type: string, payload: any) => void} 簡易化されたdispatch関数
 */
export const useRDispatch = (dispatch) => useCallback((type, payload) => dispatch({ type, payload }), [dispatch])

/**
 * エラーオブジェクト内に配列でエラーが存在するかを判定する関数
 *
 * @param {Object<string, any[]>} errors - エラーオブジェクト
 * @returns {boolean} エラーが存在すればtrue、なければfalse
 */
export function hasErrors(errors) {
  return Object.values(errors).some((arr) => Array.isArray(arr) && arr.length > 0)
}

/**
 * セグメント情報のメインコンポーネント
 *
 * @param {Object} props - コンポーネントのprops
 * @param {string} props.pageTitle - ページタイトル
 * @param {string} props.corporationNumber - 法人番号
 * @param {string} props.userId - ユーザーID
 * @param {string} props.userName - ユーザー名
 * @param {function(string, any):void} props.navigate - 画面遷移関数
 * @param {function(Object):void} props.showModal - モーダル表示関数
 * @param {Object} props.modalExtra - モーダル拡張オブジェクト
 * @returns {JSX.Element | null} セグメント情報画面のJSXまたは読み込み中はnull
 */
const SegmentInfoContent = (props) => {
  const [isLoaded, setIsLoaded] = useState(false)
  const [master, setMaster] = useState([])
  const [clientError, setClientError] = useState({})
  const [serverError, setServerError] = useState({})

  /** @type {segmentState} */
  const initialState = []

  /** @type {ReturnType<typeof useReducer<typeof segmentReducer, typeof initialState>>} */
  const [segmentState, _dispatch] = useReducer(segmentReducer, initialState)
  const localDispatch = useRDispatch(_dispatch)

  // クライアントエラー判定
  const isClientError = hasErrors(clientError)

  // 標準フォーマット利用項目
  const itemCategory = 'セグメント'

  useEffect(() => {
    document.title = props.pageTitle + titleName.common
    init()
  }, [])

  /**
   * 初期データ取得処理
   *
   * @async
   * @returns {Promise<void>}
   */
  const init = async () => {
    const pMaster = fetchMaster(props.corporationNumber)
    const pSegment = fetchSegment(props.corporationNumber)

    const [master, segment] = await Promise.all([pMaster, pSegment])

    setMaster(master)

    // ソートと表示用の加工
    if (segment && segment.length) {
      // DisplayOrderでソート（もしDisplayOrderがあれば）
      segment.sort((a, b) => (a.DisplayOrder ?? 0) - (b.DisplayOrder ?? 0))

      const processedData = segment.map((row) => ({
        searchKey: row.SearchKey,
        segmentCode: row.SegmentCode,
        segmentName: row.SegmentName,
        validStartDate: row.ValidStartDate,
        validEndDate: row.ValidEndDate,
        DisplayOrder: row.DisplayOrder ?? 0
      }))

      setSegmentInfoInitialData(processedData)
      localDispatch('INIT_STATE', processedData)
    } else {
      localDispatch('INIT_STATE', [])
    }

    setIsLoaded(true)
  }

  /**
   * ドラッグ終了時の処理（並び替え）
   *
   * @param {import('@dnd-kit/core').DragEndEvent} e - ドラッグ終了イベント
   */
  const handleDragEnd = (e) => {
    const { active, over } = e
    if (!over || active.id === over.id) return

    const oldIndex = segmentState.findIndex((item) => item.searchKey === active.id)
    const newIndex = segmentState.findIndex((item) => item.searchKey === over.id)

    if (oldIndex !== newIndex) {
      localDispatch('MOVE_ITEM', { oldIndex, newIndex })
    }
  }

  /**
   * レコード削除処理（削除アクションをdispatch）
   *
   * @param {SegmentState.searchKey} searchKey
   */
  const handleDelete = useCallback(
    (searchKey) => {
      localDispatch('DELETE_ITEM', searchKey)
    },
    [localDispatch]
  )

  /**
   * レコードリストア処理（復帰アクションをdispatch）
   *
   * @param {SegmentState.searchKey} searchKey
   */
  const handleRestore = useCallback(
    (searchKey) => {
      localDispatch('RESTORE_ITEM', searchKey)
    },
    [localDispatch]
  )

  /**
   * レコード追加処理（登録アクションをdispatch）
   *
   * @param {SegmentState} item
   */
  const handleRegiste = useCallback(
    (item) => {
      localDispatch('ADD_ITEM', item)
    },
    [localDispatch]
  )

  /**
   * レコード更新処理（更新アクションをdispatch）
   *
   * @param {SegmentState} item
   */
  const handleUpdate = useCallback(
    (item) => {
      localDispatch('UPDATE_ITEM', item)
    },
    [localDispatch]
  )

  /**
   * クライアントエラー設定処理（エラーセットアクションをdispatch）
   *
   * @param {SegmentState} errorIds
   */
  const handleSetClientError = useCallback(
    (errorIds) => {
      localDispatch('SET_CLIENT_ERROR', errorIds)
    },
    [localDispatch]
  )

  /**
   * 入力チェック処理
   *
   * @async
   * @returns {Promise<boolean>} エラーがあればtrue、なければfalse
   */
  const _checkInput = async () => {
    const errors = {
      segmentTable: []
    }

    // 標準フォーマット利用項目管理取得
    const standardFormatUsageItems = await fetchStandardFormatUsageItems(props.corporationNumber, itemCategory)

    // セグメント存在チェック
    const errorIds = getConflictEditedSegmentIds(standardFormatUsageItems, segmentState, getInitialDataBySegment)

    if (errorIds.length > 0) {
      // エラーステータス更新
      handleSetClientError(errorIds)
      errors.segmentTable.push(getMessage('E011010'))
    }

    if (hasErrors(errors)) {
      // エラーメッセージ設定
      setClientError(errors)
      return true
    } else {
      // エラークリア
      handleSetClientError([])
      setClientError({})
      return false
    }
  }

  /**
   * セグメントテーブルの表示コンポーネント
   *
   * @returns {JSX.Element}
   */
  const SegmentTable = () => {
    return (
      <>
        {segmentState.length ? (
          <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <div className="setting-table is-segment">
              <table className="setting-table__table table-fixed w-full form-control">
                <colgroup>
                  <col className="w-[8%]" />
                  <col className="w-[52%]" />
                  <col className="w-[20%]" />
                  <col className="w-[20%]" />
                </colgroup>
                <thead>
                  <tr>
                    <th className="!text-center">↑↓</th>
                    <th>セグメント名</th>
                    <th>有効開始月</th>
                    <th>有効終了月</th>
                  </tr>
                </thead>
                <tbody className="text-caption-1">
                  <SortableContext
                    items={segmentState.map((item) => item.searchKey)}
                    strategy={verticalListSortingStrategy}
                  >
                    {segmentState.map((item) => (
                      <SortableItem
                        key={item.searchKey}
                        searchKey={item.searchKey}
                        seg={item}
                        onDelete={handleDelete}
                        onRestore={handleRestore}
                        openSegmentModal={openSegmentModal}
                      />
                    ))}
                  </SortableContext>
                </tbody>
              </table>
              <ShowErrors clientErrors={clientError} fieldName="segmentTable" />
            </div>
          </DndContext>
        ) : (
          <div className="text-center my-10 pt-10">
            <span className="text-h5 red--text">セグメントが未登録です。</span>
          </div>
        )}
      </>
    )
  }

  /**
   * セグメントモーダル開閉用関数を返すカスタムフック
   *
   * @returns {(mode: string, updateData?: SegmentState | null) => void} モーダル開閉関数
   */
  const useModalContent = () => {
    const { _closeUpModal } = props.modalExtra

    const onClose = () => {
      _closeUpModal()
    }

    /**
     * @function セグメント設定ダイアログの表示
     * @param {string} mode - 操作種別（regist: 登録、update: 更新）
     * @param {SegmentState} updateData 更新データ
     */
    const openSegmentModal = (mode, updateData = null) => {
      const key = Math.random()
      const submitHandler = mode === 'update' ? handleUpdate : handleRegiste

      props.showModal({
        title: 'セグメント設定',
        body: (
          <SegmentModalContent
            key={key}
            segmentInfo={segmentState}
            updateData={updateData}
            editMode={mode}
            onClose={onClose}
            onSubmit={(data) => {
              submitHandler(data)
              _closeUpModal()
            }}
          />
        ),
        isFooter: false,
        isCancel: false,
        isWide: false
      })
    }

    return openSegmentModal
  }

  /**
   * 更新前のAPI送信用データチェック
   *
   * @async
   * @returns {Promise<boolean>} チェックエラーがあればtrue、なければfalse
   */
  const prepareCheck = async () => {
    // サーバーエラーメッセージをクリア
    setServerError({})
    // チェック処理
    const hasError = await _checkInput()
    if (hasError) {
      return true
    }

    return false
  }

  /**
   * セグメント情報更新処理
   *
   * @async
   * @returns {Promise<void>}
   */
  const handleSegmentTypeUpdate = async () => {
    // チェック処理
    const hasError = await prepareCheck()
    if (hasError) {
      return
    }

    // API送信用に加工されたファイルデータ
    const payload = prepareFileData(segmentState)
    try {
      // セグメント更新
      // 更新前処理
      const res = await updateSegment(props.corporationNumber, payload)
      if (!res) throw res
      props.navigate('/master/masterList', { state: { message: 'データ反映を開始しました。' } })
    } catch (error) {
      const serverErrorMsg = getMessage('E011012')
      setServerError({ messages: [serverErrorMsg] })
      errorLog('E000001', props.userId, viewId.segmentInfo, 'updateSegment', error)
    }
  }

  const openSegmentModal = useModalContent()

  /**
   * データ更新の確認モーダルを表示する関数
   *
   * @async
   * @returns {Promise<void>}
   */
  async function openConfirmUpdateModal() {
    const { promisify } = modifyClassList('.modal-window', ['w-[900px]'])

    await promisify((res) => {
      props.showModal({
        title: 'マスター設定値登録の確認',
        onClose: res,
        isWide: false,
        description: (
          <React.Fragment>
            <div className="text-center">設定値を登録してダッシュボードに反映します。よろしいですか？</div>
            <div className="pt-5 text-center">
              ※ダッシュボードへの反映には5分～10分程度かかります
              <br />
              マスター管理画面のステータスから反映状況を確認してください
            </div>
          </React.Fragment>
        ),
        button: [
          {
            name: '実行',
            className: 'footer-btn button_base',
            click: () => handleSegmentTypeUpdate()
          }
        ]
      })
    })
  }

  if (!isLoaded) {
    return null
  }
  return (
    <React.Fragment>
      <main className="page-container">
        <section className="page-content">
          <div className="centered-container">
            {/* 上階層に戻る */}
            <div className="bg main">
              <div className="back-upper-level py-8 pl-8 border-b border-blue2-lighten-20">
                <Link color="inherit" variant="button" underline="none" component={RouterLink} to="/master/masterList">
                  <span className="back-upper-level__link link--text hover:lighten-70 flex items-center">
                    <span className="icon-arrow-circle icon-32px mr-4 rotate-180 relative after:absolute after:bg-white after:w-full after:h-full after:rounded-full after:left-0 after:-z-10"></span>
                    <span className="text-h5 font-bold">{titleName.masterList}画面に戻る</span>
                  </span>
                </Link>
              </div>
            </div>
            {/* コンテンツヘッダ */}
            <header className="page-header flex justify-center py-1">
              <h1 className="page-header__name text-h1 grey--text py-8">
                {masterClassification[master.Classification]?.name || ''}
              </h1>
            </header>
            <div className="page-body">
              <ShowServerErrors serverErrors={serverError} />
              {isClientError && (
                <div>
                  <span style={{ color: 'red' }}>入力内容に誤りがあります。</span>
                </div>
              )}
              <div className="box-xy-bordered rounded-t-3xl rounded-b-2xl pt-10 pb-8 px-16 border-blue1 base-table-header">
                <h2 className="box-bordered-heading blue1 white--text text-h3 font-medium pt-2.5 pb-3 px-14 -mt-10 -mx-16 rounded-t-2xl">
                  セグメント情報の設定
                </h2>
                <>
                  {/* 説明文は常に表示 */}
                  <div className="flex items-center justify-between my-6">
                    セグメントの表示順と有効期間を設定します。
                    <br />
                    表示順を変更するには「≡」を上下にドラッグ＆ドロップしてください。
                    <br />
                    設定した順序で「分析対象の絞り込み」画面および「権限グループ設定」画面が表示されます。
                    <br />
                    有効開始/終了月は、レポートデータ作成時のチェックや上記画面の表示に使用されます。
                  </div>

                  <div className="flex justify-between gap-x-5 mb-3 items-center btn-group !float-none">
                    <span className="text-body-2-medium grow border-r border-blue2-lighten-20">
                      登録数：{segmentState.length}件
                    </span>

                    <Link
                      className="header-btn !p-0"
                      color="inherit"
                      variant="button"
                      underline="none"
                      component={'button'}
                      onClick={() => openSegmentModal('regist')}
                    >
                      <span className="header-btn white--text py-1.5 pl-7 pr-10 inline-flex items-center">
                        <span className="icon-plus-line icon-32px mr-1.5 my-px"></span>
                        <span className="-translate-y-px text-h7">新規追加</span>
                      </span>
                    </Link>
                  </div>

                  {/* テーブルまたは未登録メッセージを切替 */}
                  <SegmentTable />
                </>
                <div className="content-footer mt-16 mb-8">
                  <div className="border-t border-blue1" />
                  {/*  ===== フッターボタン ===== */}
                  <div className="footer_button_area mt-8">
                    <Link
                      color="inherit"
                      variant="button"
                      underline="none"
                      component={RouterLink}
                      to="/master/masterList"
                    >
                      <button type="button" className="footer-btn button_back">
                        キャンセル
                      </button>
                    </Link>
                    <SpinnerButton
                      name="登録"
                      className="footer-btn button_base"
                      disabled={segmentState.length === 0}
                      onClick={() => openConfirmUpdateModal()}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </React.Fragment>
  )
}

/**
 * セグメント情報画面のラッパーコンポーネント
 *
 * @returns {JSX.Element} セグメント情報画面
 */
const SegmentInfo = () => {
  const state = useSelector((state) => state)
  const navigate = useNavigate()
  const showModal = useModal()
  const modalExtra = useModalExtra()

  // 親会社でない、もしくは権限を持っていない場合は画面表示しない
  if (
    !state.company.isParent ||
    !state.authority.Master_AuthInfo.Availability ||
    !state.authority.Master_AuthInfo.Auth_Conf.Group.SegmentInfo_Set
  ) {
    return <Forbidden />
  }

  return (
    <SegmentInfoContent
      pageTitle="セグメント情報設定"
      corporationNumber={state.company.corporationNumber}
      userId={state.user.sub}
      userName={state.user.userName}
      navigate={navigate}
      showModal={showModal}
      modalExtra={modalExtra}
    />
  )
}

export default SegmentInfo
